import {
  DataGridPro,
  GridPagination,
  enUS,
  frFR,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { forwardRef, memo, useCallback, useEffect, useMemo } from "react";
import { actionColumn } from "./columns/actionColumn";
import {
  Button,
  Checkbox,
  NumberFormatter,
  Stack,
  Tooltip,
} from "@mantine/core";
import { useLanguage } from "../../stores/LanguageStore";
import TableToolbar from "./toolbar/TableToolbar";
import { initialTableState } from "./initialState";
import TableLoader from "./loader/TableLoader";
import NoRowsDisplay from "./noRowsDisplay/NoRowsDisplay";
import TablePagination from "./pagination/TablePagination";
import { useUIStore } from "../../stores/UIStore";
import "./MainTable.scss";
import { useTableHighlight } from "../../components/TableComponents/tableHooks";
import { useProfileStore } from "../../stores/UserStore";
import { updateProfile } from "../../api/UserAPI";
import { formatThousand } from "../../functions/utilFunctions";

function handleRowHeight({ densityFactor, model, rowHeightMultiplier }) {
  if (model?.isChild === true || model?.isParent === false) {
    return "auto";
  }

  return 64 * densityFactor * (rowHeightMultiplier || 1);
}

const TableCheckbox = forwardRef((props, ref) => {
  const { touchRippleRef, inputProps, ...rest } = props;
  return <Checkbox {...rest} ref={ref} />;
});

TableCheckbox.displayName = "TableCheckbox";

const getTreeDataPath = (row) => row?.path;

const MainTable = ({
  id,
  rows,
  columns,
  actionsCell,
  actionsCellWidth,
  apiRef,
  loading,
  setLoading,
  hidePerPage,
  pinnedLeft,
  pinnedRight,
  toolbarActions,
  hasFolder,
  pageName,
  defaultColumnVisibilityModel,
  onPaginationChange,
  isClient = true,
  rowCount,
  onSelectionChange,
  onFilterChange,
  onRowClick,
  onSortChange,
  pageOrderName,
  desiredOrder,
  desiredVisibleColumns,
  defaultSearch,
  defaultFilter,
  defaultSort,
  handleRowUpdate,
  handleRowUpdateError,
  handleRowUpdateStop,
  additionalFilter,
  filterModel,

  treeData,
  getRowId,

  rowHeightMultiplier,

  noRowsDisplay,
  defaultHighlight,
  setDefaultHighlight,
  setHasQuery,
  hasQuery,

  disableSearch,
  disableFilter,
  disableGotoPage,
  disablePerPage,
  disableCheckboxes,
  disableVisibleColumns,

  defaultPerPage,

  tableSx,
  whiteButtons,
  getRowClassName,
  getColumnActions,
  withSorter,
  columnVisibilityModel,
  selectionModel,

  customFilterComponent,
  autofocusSearch,
  isRowSelectable,

  listId,
  customColumnsButton,
  allow200PerPage,
  onSearchChange
}) => {
  const layoutIsVertical = useUIStore((s) => s.layoutIsVertical);
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);

  //#region Disable on URL
  const disableActions = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return (
      localStorage.getItem("disable_actions") === "true" ||
      urlParams.get("disableActions") === "true"
    );
  }, []);

  const disableSelections = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return (
      localStorage.getItem("disable_selections") === "true" ||
      urlParams.get("disableSelections") === "true"
    );
  }, []);

  //#endregion

  const COLUMNS = useMemo(() => {
    if (actionsCell && !disableActions) {
      return [...columns, actionColumn(actionsCell, actionsCellWidth)];
    }
    return columns;
  }, [actionsCell, actionsCellWidth, columns, disableActions]);

  const noRadiusLeft = useMemo(
    () => layoutIsVertical && hasFolder,
    [hasFolder, layoutIsVertical]
  );

  const groupingColDef = useMemo(() => {
    return {
      headerName: "Chevron",
      width: 1,
      minWidth: 1,
      renderHeader: () => "",
      renderCell: ({ id, rowNode, row }) => {
        return "";
      },
    };
  }, [apiRef]);

  const handleColumnOrderChange = useCallback(() => {
    if (!pageOrderName) {
      return;
    }
    const orderedFields = apiRef.current.state.columns.orderedFields;

    let newOrder = orderedFields;
    const profile = useProfileStore.getState().profile;
    const setProfile = useProfileStore.getState().setProfile;

    let newData = {
      saved_order: {
        ...(profile?.saved_order || {}),

        [pageOrderName]: {
          ...(profile?.saved_order?.[pageOrderName] || {}),
          order_columns: newOrder.filter((x) => x !== "actions"),
        },
      },
    };


    if (id === "list-content-table" && listId) {
      const savedListOrder = profile.saved_order?.contact_list_fetch_contacts_order_by_contact_list_id?.[listId];

      if (savedListOrder) {
        newData = {
          saved_order: {
            ...(profile?.saved_order || {}),

            "contact_list_fetch_contacts_order_by_contact_list_id": {
              ...(profile?.saved_order?.contact_list_fetch_contacts_order_by_contact_list_id || {}),
              [listId]: {
                ...(profile?.saved_order?.contact_list_fetch_contacts_order_by_contact_list_id?.[listId] || {}),
                order_columns: newOrder.filter((x) => x !== "actions"),
              },
            },
          },
        }
      }
    }

    updateProfile(null, newData).then((response) => {
      setProfile({ ...profile, ...newData });
    });
  }, [apiRef, id, listId, pageOrderName]);

  const handleColumnWidthChange = useCallback(
    (params) => {
      if (!pageOrderName) {
        return;
      }
      const profile = useProfileStore.getState().profile;
      const setProfile = useProfileStore.getState().setProfile;

      const lookup = apiRef.current.state.columns.lookup;

      let columnSize = {};
      Object.keys(lookup).map((key) => {
        const element = lookup[key];
        columnSize[element.field] = Math.floor(element.width);
      });

      let newData = {
        saved_order: {
          ...(profile?.saved_order || {}),

          [pageOrderName]: {
            ...(profile?.saved_order?.[pageOrderName] || {}),
            size_columns: columnSize,
          },
        },
      };

      if (id === "list-content-table") {
        const savedListOrder = profile.saved_order?.contact_list_fetch_contacts_order_by_contact_list_id?.[listId];


        if (savedListOrder) {
          newData = {
            saved_order: {
              ...(profile?.saved_order || {}),

              "contact_list_fetch_contacts_order_by_contact_list_id": {
                ...(profile?.saved_order?.contact_list_fetch_contacts_order_by_contact_list_id || {}),
                [listId]: {
                  ...(profile?.saved_order?.contact_list_fetch_contacts_order_by_contact_list_id?.[listId] || {}),
                  size_columns: columnSize,
                },
              },
            },
          }
        }
      }

      updateProfile(null, newData).then((response) => {
        setProfile({ ...profile, ...newData });
      });

      // const tableContainer = document.getElementById(id).querySelector(".MuiDataGrid-virtualScrollerContent").getBoundingClientRect().width;
      // console.log(tableContainer);
    },
    [apiRef, id, listId, pageOrderName]
  );

  useTableHighlight({
    apiRef,
    loading,
    defaultHighlight,
    setDefaultHighlight,
    treeData,
  });

  return (
    <Stack
      className="main-table"
      id={id}
      pos={"relative"}
      w={0}
      flex={"1 1 auto"}
      h={"100%"}
    >
      <DataGridPro
        apiRef={apiRef}
        columns={COLUMNS}
        rows={rows || []}
        rowCount={rowCount}
        headerHeight={64}
        disableVirtualization={true}
        getRowHeight={(params) => handleRowHeight({ ...params, rowHeightMultiplier })}
        density={profile?.table_density || "standard"}
        columnBuffer={12}
        columnThreshold={12}
        rowBuffer={50}
        rowThreshold={1}
        pagination
        keepNonExistentRowsSelected
        onCellClick={(event) => event.stopPropagation}
        checkboxSelection={
          disableCheckboxes || disableSelections ? false : true
        }
        checkboxSelectionVisibleOnly={isClient}
        // disableSelectionOnClick
        // disableRowSelectionOnClick
        disableRowSelectionOnClick
        onRowClick={onRowClick}
        pageSizeOptions={allow200PerPage ? [15, 30, 50, 100, 200] : [15, 30, 50, 100]}
        showCellRightBorder
        showColumnRightBorder
        // disableRowSelectionOnClick
        getRowClassName={(params) =>
          "table-row row-" +
          params.id +
          (params.indexRelativeToCurrentPage % 2 === 0 ? " even " : "odd ") +
          (params.id.toString().startsWith("#") ? " isChild " : "") +
          (params?.row?.stopped ? " stopped" : "") +
          (getRowClassName ? getRowClassName(params) : "")
        }
        sx={{
          border: 0,
          height: 0,
          flex: "1 1 auto",
          "& .MuiDataGrid-cell": {
            border: "none",
          },
          "& .MuiDataGrid-main": {
            background: "#fff",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            borderColor: "var(--mantine-color-primary-0) !important",
          },
          "& .table-row": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-withBorderColor": {
            borderColor: "var(--mantine-color-primary-0) !important",
          },
          "& .MuiDataGrid-pinnedColumns": {
            borderColor: "var(--mantine-color-primary-0) !important",
          },
          "& .MuiDataGrid-pinnedColumnHeaders": {
            borderColor: "var(--mantine-color-primary-0) !important",
          },
          "& .MuiDataGrid-columnHeader.custom_datafield": {
            background: "var(--mantine-color-secondary-0) ",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            opacity: 0.5,
            fontWeight: 800,
          },
          "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
            outline: "none !important",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "0 !important",
          },
          "& .MuiDataGrid-overlayWrapperInner": {
            height: "30vh !important",
          },
          ...tableSx,
        }}
        localeText={
          lang?.id === "FRA"
            ? frFR.components.MuiDataGrid.defaultProps.localeText
            : enUS.components.MuiDataGrid.defaultProps.localeText
        }
        loading={loading}
        paginationMode={isClient ? "client" : "server"}
        customFilterComponent={customFilterComponent}
        customColumnsButton={customColumnsButton}
        slots={{
          toolbar: TableToolbar,
          baseButton: Button,
          baseCheckbox: TableCheckbox,
          baseTooltip: (props) => {
            const { enterDelay, ...rest } = props;
            return (
              <Tooltip
                {...rest}
                label={props?.title}
                disabled={!props?.title}
              />
            );
          },
          loadingOverlay: TableLoader,
          noRowsOverlay: () => <NoRowsDisplay component={noRowsDisplay} />,
          pagination: (props) => (
            <GridPagination
              ActionsComponent={TablePagination}
              {...props}
              labelRowsPerPage=""
              sx={{
                width: "100%",
                position: "relative",
                // pr: "16px !important",
                ml: 0,
                "& .MuiToolbar-root": {
                  position: "static !important",
                  width: "100%",
                  padding: 0
                },
                "& .MuiInputBase-root": {
                  display: disablePerPage ? "none !important" : "flex",
                },
              }}
              labelDisplayedRows={({ from, to, count, page }) =>
                `${from}–${to} ${lang.contact_lists.content.of
                } ${formatThousand(count)}`
              }
            />
          ),
          columnFilteredIcon: () => "",
          columnHeaderFilterIconButton: () => "",
        }}
        slotProps={{
          baseButton: {
            variant: "outline",
            sm: "test",
          },
          footer: {
            sx: {
              position: "relative",
            },
          },
          columnMenu: {
            slots: {
              columnMenuFilterItem: null,
              columnMenuColumnsItem: null,
            },
          },
        }}
        initialState={initialTableState({
          disablePerPage,
          defaultPerPage,
          pinnedLeft,
          pinnedRight,
          defaultColumnVisibilityModel,
          columnVisibilityModel,
          columns,
          pageName,
          pageOrderName,
          desiredOrder,
          desiredVisibleColumns,
          defaultSearch,
          defaultFilter,
          defaultSort,
          isClient,
          listId
        })}
        columnVisibilityModel={columnVisibilityModel}
        processRowUpdate={handleRowUpdate}
        onProcessRowUpdateError={handleRowUpdateError}
        onRowEditStop={handleRowUpdateStop}
        onPaginationModelChange={onPaginationChange}
        onRowSelectionModelChange={onSelectionChange}
        rowSelectionModel={selectionModel}
        filterMode={isClient ? "client" : "server"}
        onFilterModelChange={onFilterChange}
        sortingMode={isClient ? "client" : "server"}
        onSortModelChange={onSortChange}
        disableMultipleColumnsSorting
        treeData={treeData}
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        getRowId={getRowId}
        hidePerPage={hidePerPage}
        editMode="cell"
        // RootProps
        isClient={isClient}
        pageName={pageName}
        setLoading={setLoading}
        noRadiusLeft={noRadiusLeft}
        tableId={id}
        toolbarActions={toolbarActions}
        pageOrderName={pageOrderName}
        defaultSearch={defaultSearch}
        defaultFilter={defaultFilter}
        disableSearch={disableSearch}
        // Disable Components
        disableFilter={disableFilter}
        filterModel={filterModel}
        disableGotoPage={disableGotoPage}
        disableVisibleColumns={disableVisibleColumns}
        setHasQuery={setHasQuery}
        hasQuery={hasQuery}
        additionalFilter={additionalFilter}
        whiteButtons={whiteButtons}
        getColumnActions={getColumnActions}
        withSorter={withSorter}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnWidthChange={handleColumnWidthChange}
        disableChildrenSorting
        autofocusSearch={autofocusSearch}
        isRowSelectable={isRowSelectable}
        onSearchChange={onSearchChange}
      />
    </Stack>
  );
};

export default memo(MainTable);
